import type { StaffAlertDto, StaffAlertFeed, StaffAlertSeverity } from '@his/shared';

export const BOLD_OPEN = '';
export const BOLD_CLOSE = '';
const MARKERS = /[]/g;
const MAX_TIMER_MS = 2_147_000_000;

export interface MessageSegment {
  text: string;
  bold: boolean;
  at: number;
}

export function boldParams(params: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(params).map(([key, value]) => [key, `${BOLD_OPEN}${String(value).replace(MARKERS, '')}${BOLD_CLOSE}`]));
}

export function messageSegments(text: string): MessageSegment[] {
  const segments: MessageSegment[] = [];
  let at = 0;
  while (at < text.length) {
    const open = text.indexOf(BOLD_OPEN, at);
    if (open === -1) {
      segments.push({ text: text.slice(at), bold: false, at });
      break;
    }
    if (open > at) segments.push({ text: text.slice(at, open), bold: false, at });
    const close = text.indexOf(BOLD_CLOSE, open + 1);
    const end = close === -1 ? text.length : close;
    segments.push({ text: text.slice(open + 1, end), bold: true, at: open });
    at = end + 1;
  }
  return segments.filter((segment) => segment.text.length > 0);
}

export function trayView(alerts: readonly StaffAlertDto[], options: { hideNonCritical: boolean; max: number }): { shown: StaffAlertDto[]; more: number; hidden: number } {
  const eligible = options.hideNonCritical ? alerts.filter((alert) => alert.severity === 'critical') : [...alerts];
  const shown = eligible.slice(0, options.max);
  return { shown, more: eligible.length - shown.length, hidden: alerts.length - eligible.length };
}

export function severityTone(severity: StaffAlertSeverity): 'destructive' | 'warning' | 'info' {
  if (severity === 'critical') return 'destructive';
  return severity === 'warning' ? 'warning' : 'info';
}

export function unreadLabel(count: number | null | undefined): string | null {
  if (!count || count <= 0) return null;
  return count > 99 ? '99+' : String(count);
}

export function withoutAlert(pages: readonly StaffAlertFeed[], alertId: string): StaffAlertFeed[] {
  let removed = false;
  const next = pages.map((page) => {
    const data = page.data.filter((alert) => alert.id !== alertId);
    if (data.length !== page.data.length) removed = true;
    return { ...page, data };
  });
  if (!removed || next.length === 0) return next;
  const first = next[0];
  return [{ ...first, unread: first.unread === null ? null : Math.max(0, first.unread - 1) }, ...next.slice(1)];
}

export function delayUntil(iso: string | null | undefined, now: number = Date.now(), slackMs = 1_000): number | null {
  if (!iso) return null;
  const at = Date.parse(iso);
  if (!Number.isFinite(at)) return null;
  return Math.min(Math.max(at - now, 0) + slackMs, MAX_TIMER_MS);
}
